import React, { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FiArrowRight, FiPhone } from 'react-icons/fi';
import { servicePages } from '../content/servicePages';
import './ServicePage.scss';

function ServiceCta({ compact = false }) {
  return (
    <aside className={`service-cta${compact ? ' service-cta--compact' : ''}`}>
      <div>
        <p className="service-cta__eyebrow">Konsultacja prawna</p>
        <h2>Opowiedz krótko, czego dotyczy sprawa</h2>
        <p>
          Po zapoznaniu się z wiadomością kancelaria skontaktuje się, aby
          ustalić możliwy zakres pomocy i termin konsultacji.
        </p>
      </div>
      <div className="service-cta__actions">
        <Link className="service-button service-button--primary" to="/kontakt#contact-form-id">
          Napisz do kancelarii <FiArrowRight aria-hidden="true" />
        </Link>
        <a className="service-button service-button--secondary" href="tel:531173467">
          <FiPhone aria-hidden="true" /> +48 531 173 467
        </a>
      </div>
    </aside>
  );
}

export function ServicePage() {
  const { slug } = useParams();
  const service = servicePages[slug];

  useEffect(() => {
    if (!service) return undefined;

    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute('content');

    document.title =
      service.seoTitle || `${service.title} | Kancelaria Radcy Prawnego`;

    if (description && service.seoDescription) {
      description.setAttribute('content', service.seoDescription);
    }

    return () => {
      document.title = previousTitle;
      if (description && previousDescription) {
        description.setAttribute('content', previousDescription);
      }
    };
  }, [service]);

  if (!service) {
    return <Navigate to="/zakres-uslug" replace />;
  }

  return (
    <article className="service-page">
      <nav className="service-breadcrumbs" aria-label="Okruszki">
        <Link to="/zakres-uslug">Zakres usług</Link>
        <span aria-hidden="true">/</span>
        <span>{service.title}</span>
      </nav>

      <header className="service-page__hero">
        <p className="service-page__eyebrow">{service.eyebrow}</p>
        <h1>{service.title}</h1>
        <p className="service-page__lead">{service.intro}</p>
        <Link className="service-page__hero-link" to="/kontakt#contact-form-id">
          Umów konsultację <FiArrowRight aria-hidden="true" />
        </Link>
      </header>

      <section className="service-section">
        <div className="service-section__number" aria-hidden="true">
          01
        </div>
        <div>
          <h2>W jakich sprawach mogę pomóc?</h2>
          <ul className="service-list">
            {service.situations.map((situation) => (
              <li key={situation}>{situation}</li>
            ))}
          </ul>
        </div>
      </section>

      <ServiceCta compact />

      {service.contentSections?.map((section) => (
        <section className="service-detail" key={section.title}>
          <h2>{section.title}</h2>
          {section.paragraphs?.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          {section.points && (
            <ul className="service-list service-detail__list">
              {section.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          )}
          {section.note && <p className="service-detail__note">{section.note}</p>}
        </section>
      ))}

      <section className="service-section">
        <div className="service-section__number" aria-hidden="true">
          02
        </div>
        <div>
          <h2>Jak wygląda współpraca?</h2>
          <ol className="service-steps">
            <li>
              <strong>Krótki opis sytuacji.</strong> Prześlij najważniejsze
              informacje przez formularz albo skontaktuj się telefonicznie.
            </li>
            <li>
              <strong>Wstępne ustalenie zakresu.</strong> Określimy, jakie
              dokumenty są potrzebne i czego powinna dotyczyć konsultacja.
            </li>
            <li>
              <strong>Analiza i rekomendacja.</strong> Po zapoznaniu się z
              materiałami otrzymasz informację o możliwych dalszych krokach.
            </li>
            <li>
              <strong>Dalsza pomoc.</strong> W zależności od sprawy może to być
              pismo, negocjacje, ugoda albo reprezentacja w postępowaniu.
            </li>
          </ol>
        </div>
      </section>

      <section className="service-section">
        <div className="service-section__number" aria-hidden="true">
          03
        </div>
        <div>
          <h2>Co przygotować do pierwszego kontaktu?</h2>
          <p>
            Na początku nie trzeba przesyłać całych akt. Wystarczy krótki opis
            sprawy, ważne daty i informacja, jakie dokumenty są dostępne.
          </p>
          <ul className="service-list service-list--documents">
            {service.documents.map((document) => (
              <li key={document}>{document}</li>
            ))}
          </ul>
          {service.documentNotice && (
            <p className="service-page__document-notice">
              {service.documentNotice}
            </p>
          )}
          <p className="service-page__notice">
            Informacje na stronie mają charakter ogólny. Ocena właściwego
            sposobu działania wymaga poznania konkretnej sytuacji i dokumentów.
          </p>
        </div>
      </section>

      {service.faqs && (
        <section className="service-faq">
          <p className="service-page__eyebrow">Najczęstsze pytania</p>
          <h2>{service.faqHeading || 'O co pytają klienci?'}</h2>
          <div className="service-faq__items">
            {service.faqs.map((faq) => (
              <details key={faq.question}>
                <summary>{faq.question}</summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {service.relatedServices && (
        <nav className="service-related" aria-label="Powiązane usługi">
          <p className="service-page__eyebrow">Powiązane usługi</p>
          <div>
            {service.relatedServices.map((relatedService) => (
              <Link key={relatedService.to} to={relatedService.to}>
                {relatedService.label} <FiArrowRight aria-hidden="true" />
              </Link>
            ))}
          </div>
        </nav>
      )}

      <ServiceCta />
    </article>
  );
}
